import { useEffect, useState } from "react";
import { useClub } from "@/contexts/ClubContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { LinkifyText } from "@/components/LinkifyText";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ChevronDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_LINES = 4;

function NewsCard({ item, clubs, getClubAccent, onImageClick }) {
  const [expanded, setExpanded] = useState(false);
  const content = item.content || "";
  const lines = content.split("\n");
  const needsTruncation = lines.length > MAX_LINES;
  const displayText = !expanded && needsTruncation ? lines.slice(0, MAX_LINES).join("\n") : content;

  return (
    <div className="p-6">
      <Badge style={{ backgroundColor: getClubAccent(item.club_id), color: '#09090b' }}
        className="text-xs font-bold uppercase mb-3">
        {clubs.find(c => c.id === item.club_id)?.name || item.club_id}
      </Badge>
      <h3 className="text-xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>
        {item.title}
      </h3>
      <div className="text-sm text-muted-foreground mb-2 leading-relaxed whitespace-pre-line">
        <LinkifyText text={displayText} />
        {!expanded && needsTruncation && <span className="text-muted-foreground/50">…</span>}
      </div>
      {needsTruncation && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors mb-3"
          data-testid={`news-expand-${item.id}`}
        >
          {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{new Date(item.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
        <span>{item.created_by}</span>
      </div>
    </div>
  );
}

export default function NewsPage() {
  usePageTitle("Neuigkeiten");
  const { activeClub, clubs, getClubAccent, setActiveClub } = useClub();
  const [news, setNews] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const params = activeClub ? `?club_id=${activeClub}` : "";
    axios.get(`${API}/news${params}`).then(r => setNews(r.data)).catch(() => {});
  }, [activeClub]);

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="news-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
          Neuig<span style={{ color: activeClub ? getClubAccent(activeClub) : '#fbbf24' }}>keiten</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-8">Aktuelles aus dem Ninersbillard und den Vereinen</p>

        {/* Club Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="news-club-filter">
          <button onClick={() => setActiveClub(null)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${!activeClub ? 'border-primary bg-primary text-primary-foreground' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
            data-testid="news-filter-all">
            Alle
          </button>
          {clubs.map(club => (
            <button key={club.id} onClick={() => setActiveClub(activeClub === club.id ? null : club.id)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${activeClub === club.id ? '' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
              style={activeClub === club.id ? { borderColor: club.accent, backgroundColor: club.accent, color: '#09090b' } : {}}
              data-testid={`news-filter-${club.id}`}>
              {club.name}
            </button>
          ))}
        </div>

        {news.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">Keine Neuigkeiten vorhanden.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {news.map((item, i) => (
              <article key={item.id}
                className={`bg-card border border-white/5 overflow-hidden card-hover animate-fade-in-up stagger-${(i % 5) + 1}`}
                data-testid={`news-card-${item.id}`}>
                {item.image_url && (
                  <div className="aspect-video overflow-hidden cursor-pointer" onClick={() => setLightboxSrc(item.image_url)}
                    data-testid={`news-image-${item.id}`}>
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover img-zoom hover:opacity-90 transition-opacity" />
                  </div>
                )}
                <NewsCard item={item} clubs={clubs} getClubAccent={getClubAccent} onImageClick={setLightboxSrc} />
              </article>
            ))}
          </div>
        )}
      </div>

      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} alt="News Bild" onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}
